'use client';

import { useQuery } from '@tanstack/react-query';

import { useMonitoring } from '@kit/monitoring/hooks';
import { isTransientNetworkError } from '@kit/shared/lib/is-transient-network-error';
import { useSupabase } from '@kit/supabase/hooks/use-supabase';

import { formatLastSeen } from '../lib/format-last-seen';
import { useIsUserOnline } from './use-presence';

/**
 * Hook to get a user's online status and last seen time
 *
 * Combines real-time presence data with persisted last_active_at
 * to provide a complete status picture.
 *
 * @param userId - The user ID to check status for
 * @returns Object with isOnline, lastActiveAt, and formatted statusText
 */
export function useUserStatus(userId: string | null | undefined) {
  const client = useSupabase();
  const monitoring = useMonitoring();
  const isOnline = useIsUserOnline(userId);

  const { data: lastActiveAt } = useQuery({
    queryKey: ['user-last-active', userId],
    queryFn: async () => {
      if (!userId) return null;

      const { data, error } = await client
        .from('profiles')
        .select('last_active_at')
        .eq('id', userId)
        .maybeSingle();

      if (error) {
        // Skip transient browser network blips — non-actionable noise for
        // this best-effort status read; capture only genuine query errors.
        if (!isTransientNetworkError(error)) {
          void monitoring.captureException(
            new Error(`Failed to fetch last_active_at: ${error.message}`),
          );
        }
        return null;
      }

      return data?.last_active_at || null;
    },
    enabled: !!userId && !isOnline, // Only fetch if user is offline
    staleTime: 60 * 1000, // 1 minute
    gcTime: 5 * 60 * 1000, // 5 minutes cache
  });

  const statusText = isOnline ? 'Online' : formatLastSeen(lastActiveAt ?? null);

  return {
    isOnline,
    lastActiveAt,
    statusText,
  };
}
